/**
 * TTS voice selection - maps male/female preference to the most natural voice
 * available on the device.
 *
 * iOS naming:
 *  - Apple ships standard "compact" voices by default (small, robotic).
 *  - Users can download "Enhanced" / "Premium" / "Siri" voices in
 *    Settings → Accessibility → Spoken Content → Voices, which sound dramatically
 *    more natural. We prefer those when available.
 *  - The voice list is locale-aware - we accept any English locale (en-US/GB/AU/IE etc.)
 *    and pick the highest-quality one matching the gender preference.
 *
 * Android: similar name/locale matching - newer devices ship "Google" neural voices.
 */
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { Capacitor } from '@capacitor/core';

interface RawVoice {
  name: string;
  lang: string;
  voiceURI?: string;
  default?: boolean;
  localService?: boolean;
}

let cachedVoices: RawVoice[] | null = null;

// Best-quality natural-sounding female voices, in priority order.
// Premium/Enhanced/Siri variants come first, then standard fallbacks.
const FEMALE_PREFERRED = [
  // Premium (very natural, neural)
  'ava (premium)', 'zoe (premium)', 'allison (premium)', 'susan (premium)',
  'serena (premium)', 'kate (premium)', 'martha (premium)',
  // Siri voices (also very natural)
  'siri female', 'siri voice 2', 'siri voice 4',
  // Enhanced (better than compact, smaller than premium)
  'ava (enhanced)', 'samantha (enhanced)', 'allison (enhanced)',
  'serena (enhanced)', 'kate (enhanced)', 'moira (enhanced)',
  // Standard female voices
  'ava', 'samantha', 'allison', 'serena', 'kate', 'moira',
  'karen', 'victoria', 'tessa', 'veena', 'fiona',
];

// Best-quality natural-sounding male voices, in priority order.
const MALE_PREFERRED = [
  // Premium
  'tom (premium)', 'evan (premium)', 'aaron (premium)', 'arthur (premium)',
  'daniel (premium)', 'oliver (premium)',
  // Siri
  'siri male', 'siri voice 1', 'siri voice 3',
  // Enhanced
  'tom (enhanced)', 'evan (enhanced)', 'aaron (enhanced)',
  'daniel (enhanced)', 'oliver (enhanced)', 'alex (enhanced)',
  // Standard male voices
  'tom', 'evan', 'aaron', 'arthur', 'alex', 'daniel', 'oliver', 'fred',
];

const FEMALE_FALLBACK_TOKENS = ['female', 'woman'];
const MALE_FALLBACK_TOKENS = ['male', 'man'];

// Voices we should never pick - they sound robotic/poor.
const BLOCKLIST = ['eddy', 'flo', 'grandma', 'grandpa', 'rocko', 'shelley', 'sandy', 'reed', 'jester', 'whisper', 'good news', 'bad news', 'bahh', 'bells', 'boing', 'bubbles', 'cellos', 'organ', 'trinoids', 'zarvox', 'wobble', 'pipe organ', 'novelty'];

export async function getTTSVoiceIndex(
  preference: 'male' | 'female',
  lang = 'en-US'
): Promise<number | undefined> {
  if (!Capacitor.isNativePlatform()) {
    return undefined;
  }
  try {
    if (!cachedVoices) {
      const { voices } = await TextToSpeech.getSupportedVoices();
      cachedVoices = voices as RawVoice[];
    }
    const langPrefix = lang.split('-')[0].toLowerCase();
    const englishVoices = cachedVoices
      .map((v, i) => ({ v, i }))
      .filter(({ v }) => v.lang.toLowerCase().startsWith(langPrefix))
      .filter(({ v }) => {
        const nameLower = v.name.toLowerCase();
        return !BLOCKLIST.some((b) => nameLower.includes(b));
      });

    const preferred = preference === 'male' ? MALE_PREFERRED : FEMALE_PREFERRED;
    const fallbackTokens = preference === 'male' ? MALE_FALLBACK_TOKENS : FEMALE_FALLBACK_TOKENS;
    const oppositePreferred = preference === 'male' ? FEMALE_PREFERRED : MALE_PREFERRED;

    // 1. Exact-priority match (Premium → Siri → Enhanced → standard)
    for (const target of preferred) {
      const match = englishVoices.find(({ v }) => v.name.toLowerCase() === target);
      if (match) return match.i;
    }
    // 2. Substring match against the same priority list
    for (const target of preferred) {
      const match = englishVoices.find(({ v }) => v.name.toLowerCase().includes(target));
      if (match) return match.i;
    }
    // 3. Generic gender-token match (e.g. "English Female")
    for (const tok of fallbackTokens) {
      const match = englishVoices.find(({ v }) => v.name.toLowerCase().includes(tok));
      if (match) return match.i;
    }
    // 4. Anything English that isn't on the opposite gender's list
    const oppositeSet = new Set(oppositePreferred);
    const neutral = englishVoices.find(({ v }) => !oppositeSet.has(v.name.toLowerCase()));
    if (neutral) return neutral.i;
    // 5. Last resort: first English voice
    return englishVoices[0]?.i;
  } catch {
    return undefined;
  }
}
